import React from "react";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { linearRate } from "@/lib/linearPricing";

const TierEditor = ({ tiers, onChange }) => {
  const upd = (idx, k, v) => onChange(tiers.map((t, j) => (j === idx ? { ...t, [k]: v } : t)));
  return (
    <div className="space-y-2" data-testid="tier-editor">
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[11px] uppercase tracking-wider text-slate-500">
        <span>Até (metros)</span><span>R$ por metro</span><span />
      </div>
      {tiers.map((t, idx) => (
        <div key={`tier-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
          <Input type="number" step="0.01" placeholder="sem limite" value={t.up_to ?? ""}
            onChange={(e) => upd(idx, "up_to", e.target.value)} data-testid={`tier-upto-${idx}`} />
          <Input type="number" step="0.01" value={t.price_per_m ?? ""}
            onChange={(e) => upd(idx, "price_per_m", e.target.value)} data-testid={`tier-price-${idx}`} />
          <Button type="button" size="icon" variant="ghost" className="text-rose-600" onClick={() => onChange(tiers.filter((_, j) => j !== idx))} data-testid={`tier-del-${idx}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...tiers, { up_to: "", price_per_m: "" }])} data-testid="tier-add-btn">
        <Plus className="w-3 h-3 mr-1" /> Faixa
      </Button>
      <p className="text-[11px] text-slate-500">Deixe "Até" vazio na última faixa para valer acima do maior limite.</p>
    </div>
  );
};

const MaterialFields = ({ form, set }) => (
  <div className="grid grid-cols-3 gap-2">
    <div><Label className="text-xs">Material R$/m</Label>
      <Input type="number" step="0.01" value={form.material_cost_m ?? 0} onChange={(e) => set("material_cost_m", e.target.value)} data-testid="svc-material" /></div>
    <div><Label className="text-xs">Mão de obra R$/m</Label>
      <Input type="number" step="0.01" value={form.labor_cost_m ?? 0} onChange={(e) => set("labor_cost_m", e.target.value)} data-testid="svc-labor" /></div>
    <div><Label className="text-xs">Margem %</Label>
      <Input type="number" step="0.1" value={form.margin_pct ?? 0} onChange={(e) => set("margin_pct", e.target.value)} data-testid="svc-margin" /></div>
    <div className="col-span-3 text-xs text-slate-600">Preço por metro: <b className="kpi-num">{brl(linearRate(form, 1))}</b></div>
  </div>
);

export const ServicePricingFields = ({ form, setForm }) => {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mode = form.pricing_mode || "fixed";
  return (
    <div className="col-span-2 space-y-3 border rounded-lg p-3 bg-slate-50" data-testid="service-pricing">
      <div><Label>Forma de cobrança</Label>
        <Select value={mode} onValueChange={(v) => set("pricing_mode", v)}>
          <SelectTrigger data-testid="svc-pricing-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Preço fixo</SelectItem>
            <SelectItem value="linear_tiers">Metro linear — faixas de preço</SelectItem>
            <SelectItem value="linear_material">Metro linear — material + mão de obra</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "linear_tiers" && <TierEditor tiers={form.tiers || []} onChange={(t) => set("tiers", t)} />}
      {mode === "linear_material" && <MaterialFields form={form} set={set} />}
      {mode !== "fixed" && (
        <div><Label className="text-xs">Valor mínimo do serviço (R$)</Label>
          <Input type="number" step="0.01" value={form.min_charge ?? 0} onChange={(e) => set("min_charge", e.target.value)} data-testid="svc-min-charge" /></div>
      )}
    </div>
  );
};
